import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

/**
 * useTableStore - Manages table UI state (selection, expansion, panel visibility)
 *
 * This store holds all table-specific UI state that was previously scattered across
 * NTSelectionContext. It enables fine-grained subscriptions so components only
 * re-render when the data they use actually changes.
 *
 * Migration from NTSelectionContext:
 * - cellStates.selected → selectedCell
 * - cellStates.selectedColumn → selectedColumn
 * - expandedRows (kept same)
 * - actions.detailsPanel → detailsPanelOpen
 */

export interface TableStoreState {
  // ===== Selection State =====
  selectedCell: string | null;
  selectedColumn: string | number | null;
  selectionHistory: (string | null)[];
  historyIndex: number;

  // ===== Expansion State =====
  expandedRows: string[];

  // ===== Panel Visibility =====
  detailsPanelOpen: boolean;
  auditModalOpen: boolean;
  transactionModalOpen: boolean;

  // ===== Display Settings =====
  sparklineType: string | null;
  budgetAvailable: boolean;

  // ===== Search =====
  searchQuery: string | null;
  searchResults: string[];

  // ===== Actions =====
  selectCell: (cellId: string | null, updateHistory?: boolean) => void;
  selectColumn: (columnIndex: string | number | null) => void;
  toggleRowExpanded: (rowId: string) => void;
  setDetailsPanelOpen: (open: boolean) => void;
  setAuditModalOpen: (open: boolean) => void;
  setTransactionModalOpen: (open: boolean) => void;
  setSparklineType: (type: string | null) => void;
  setBudgetAvailable: (available: boolean) => void;
  setSearchQuery: (query: string | null) => void;
  setSearchResults: (results: string[]) => void;
  reset: () => void;
}

export const useTableStore = create(
  subscribeWithSelector<TableStoreState>((set, get) => ({
    // ===== Initial State =====
    selectedCell: null,
    selectedColumn: null,
    selectionHistory: [],
    historyIndex: -1,
    expandedRows: [],
    detailsPanelOpen: false,
    auditModalOpen: false,
    transactionModalOpen: false,
    sparklineType: null,
    budgetAvailable: false,
    searchQuery: null,
    searchResults: [],

    // ===== Selection Actions =====
    selectCell: (cellId: string | null, updateHistory = false) =>
      set(state => {
        const newHistory = updateHistory
          ? [...state.selectionHistory.slice(0, state.historyIndex + 1), cellId]
          : state.selectionHistory;

        return {
          selectedCell: cellId,
          selectionHistory: newHistory,
          historyIndex: updateHistory ? newHistory.length - 1 : state.historyIndex,
        };
      }),

    selectColumn: (columnIndex: string | number | null) => set({ selectedColumn: columnIndex }),

    // ===== Expansion Actions =====
    toggleRowExpanded: (rowId: string) =>
      set(state => ({
        expandedRows: state.expandedRows.includes(rowId)
          ? state.expandedRows.filter(id => id !== rowId)
          : [...state.expandedRows, rowId],
      })),

    // ===== Panel Actions =====
    setDetailsPanelOpen: (open: boolean) => set({ detailsPanelOpen: open }),
    setAuditModalOpen: (open: boolean) => set({ auditModalOpen: open }),
    setTransactionModalOpen: (open: boolean) => set({ transactionModalOpen: open }),

    // ===== Display Settings =====
    setSparklineType: (type: string | null) => set({ sparklineType: type }),
    setBudgetAvailable: (available: boolean) => set({ budgetAvailable: available }),

    // ===== Search Actions =====
    setSearchQuery: (query: string | null) => set({ searchQuery: query }),
    setSearchResults: (results: string[]) => set({ searchResults: results }),

    // ===== Reset =====
    reset: () =>
      set({
        selectedCell: null,
        selectedColumn: null,
        selectionHistory: [],
        historyIndex: -1,
        expandedRows: [],
        detailsPanelOpen: false,
        auditModalOpen: false,
        transactionModalOpen: false,
        sparklineType: null,
        budgetAvailable: false,
        searchQuery: null,
        searchResults: [],
      }),
  }))
);

// ===== Convenient Selectors for Performance =====
// Use these to subscribe to only the data your component needs
export const useSelectedCell = () => useTableStore(state => state.selectedCell);
export const useSelectedColumn = () => useTableStore(state => state.selectedColumn);
export const useExpandedRows = () => useTableStore(state => state.expandedRows);
export const useDetailsPanelOpen = () => useTableStore(state => state.detailsPanelOpen);
export const useAuditModalOpen = () => useTableStore(state => state.auditModalOpen);
export const useTransactionModalOpen = () => useTableStore(state => state.transactionModalOpen);
export const useSearchQuery = () => useTableStore(state => state.searchQuery);
